import CountUp from 'react-countup';
import { ShieldCheck } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetScreeningStats } from '@/reactQuery/get';
import useTranslation from '@/hooks/useTranslation';
import StatisticCard from './StatisticCard';
import AnalysisSection from '@/components/analysis/AnalysisSection';

interface ScreeningInsightsProps {
  projectId: string;
}

function ScreeningInsights({ projectId }: ScreeningInsightsProps) {
  const { lang } = useTranslation();
  const { data, isLoading } = useGetScreeningStats(projectId);

  const quotaPercentage = data?.quota?.percentage ?? 0;

  const progressBarColor =
    quotaPercentage >= 100
      ? 'bg-red-500'
      : quotaPercentage >= 80
        ? 'bg-amber-500'
        : 'bg-emerald-500';

  if (isLoading) {
    return (
      <div className="rounded-xl border border-neutral-200/80 bg-white p-5 shadow-sm space-y-4">
        <Skeleton className="h-5 w-48 rounded" />
        <Skeleton className="h-3 w-full rounded-full" />
        <div className="grid grid-cols-5 gap-3 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex flex-col gap-3 rounded-xl border border-neutral-200/80 bg-white p-5"
            >
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <AnalysisSection
      title={lang.get('msg.screeningQuotaInsights')}
      icon={<ShieldCheck className="h-5 w-5" />}
      fullWidth
      defaultOpen
    >
      <div className="space-y-4">
        {/* Quota progress bar */}
        {data.quota.max > 0 && (
          <div className="flex flex-col gap-1.5 rounded-lg border border-neutral-100 bg-neutral-50/50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-neutral-700">{lang.get('msg.respondents')}</span>
              <span className="text-neutral-500">
                {data.quota.current} / {data.quota.max} ({Math.round(quotaPercentage)}%)
              </span>
            </div>
            <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-neutral-200/60">
              <div
                className={`h-full rounded-full transition-all ${progressBarColor}`}
                style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Screening summary cards */}
        <div className="grid grid-cols-5 gap-3 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
          <StatisticCard
            isLoading={false}
            bgColor="#3b82f6"
            title={lang.get('msg.screeningTotalScreened')}
            tooltip={lang.get('msg.screeningTotalScreenedTooltip')}
          >
            <CountUp end={data.screening.total_screened} />
          </StatisticCard>

          <StatisticCard
            isLoading={false}
            bgColor="#059669"
            title={lang.get('msg.screeningPassed')}
            tooltip={lang.get('msg.screeningPassedTooltip')}
          >
            <CountUp end={data.screening.passed} />
          </StatisticCard>

          <StatisticCard
            isLoading={false}
            bgColor="#dc2626"
            title={lang.get('msg.screeningFailed')}
            tooltip={lang.get('msg.screeningFailedTooltip')}
          >
            <CountUp end={data.screening.failed} />
          </StatisticCard>

          <StatisticCard
            isLoading={false}
            bgColor="#f59e0b"
            title={lang.get('msg.screeningAbandoned')}
            tooltip={lang.get('msg.screeningAbandonedTooltip')}
          >
            <CountUp end={data.screening.abandoned} />
          </StatisticCard>

          <StatisticCard
            isLoading={false}
            bgColor="#8b5cf6"
            title={lang.get('msg.screeningPassRate')}
            tooltip={lang.get('msg.screeningPassRateTooltip')}
          >
            <CountUp end={data.screening.pass_rate} decimals={1} />%
          </StatisticCard>
        </div>
      </div>
    </AnalysisSection>
  );
}

export default ScreeningInsights;
